/**
 * SYSTELOS MONITOR - Página de Comunicações
 * 
 * @version 2.2.0
 * @date 26/01/2026
 * ADD: Ordenação clicável nas colunas da tabela
 */

import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  FileText,
  RefreshCw,
  Search,
  Clock,
  Archive,
  AlertTriangle,
  Eye,
  Check,
  Scale,
  Receipt,
  Mail,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
} from 'lucide-react'
import { AppLayout } from '@/components/layout'
import { PageContainer, PageBanner, PageSection, StatCard } from '@/components/page'
import comunicacoesService, { 
  Comunicacao, 
  ComunicacaoStats, 
  ComunicacaoFiltros,
  OrigemComunicacao 
} from '@/services/comunicacoesService'
import toast from 'react-hot-toast'

// Tipo para ordenação
type CampoOrdenacao = 'origem' | 'cliente' | 'id_externo' | 'tipo' | 'data_disponibilizacao' | 'status' | 'prioridade'
type DirecaoOrdenacao = 'asc' | 'desc'

export default function Comunicacoes() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Estados
  const [loading, setLoading] = useState(true)
  const [comunicacoes, setComunicacoes] = useState<Comunicacao[]>([])
  const [stats, setStats] = useState<ComunicacaoStats | null>(null)
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 })

  // Filtros
  const [busca, setBusca] = useState(searchParams.get('busca') || '')
  const [filtroOrigem, setFiltroOrigem] = useState<string>(searchParams.get('origem') || 'todos')
  const [filtroStatus, setFiltroStatus] = useState<string>(searchParams.get('status') || 'todos')
  const [filtroPrioridade, setFiltroPrioridade] = useState<string>(searchParams.get('prioridade') || 'todos')

  // Ordenação
  const [ordenarPor, setOrdenarPor] = useState<CampoOrdenacao>('data_disponibilizacao')
  const [direcao, setDirecao] = useState<DirecaoOrdenacao>('desc')

  // Modal de Ciência
  const [modalCiencia, setModalCiencia] = useState<{ id: string; identificador: string } | null>(null)
  const [cienciaPor, setCienciaPor] = useState('')

  // Alternar ordenação
  const handleOrdenar = (campo: CampoOrdenacao) => {
    if (ordenarPor === campo) {
      // Se já está ordenando por esse campo, inverte a direção
      setDirecao(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      // Novo campo, começa descendente
      setOrdenarPor(campo)
      setDirecao('desc')
    }
  }

  // Ícone de ordenação
  const IconeOrdenacao = ({ campo }: { campo: CampoOrdenacao }) => {
    if (ordenarPor !== campo) {
      return <ChevronsUpDown className="w-4 h-4 text-gray-400" />
    }
    return direcao === 'asc' 
      ? <ChevronUp className="w-4 h-4 text-[#0F1B3D]" />
      : <ChevronDown className="w-4 h-4 text-[#0F1B3D]" />
  }

  // Carregar comunicações
  const carregarComunicacoes = async () => {
    setLoading(true)
    try {
      const filtros: ComunicacaoFiltros = {
        page: pagination.page,
        limit: pagination.limit,
        busca: busca || undefined,
        origem: filtroOrigem as OrigemComunicacao | 'todos',
        status: filtroStatus as any,
        prioridade: filtroPrioridade as any,
        ordenar: ordenarPor,
        direcao: direcao,
      }

      const [comunicacoesRes, statsRes] = await Promise.all([
        comunicacoesService.listar(filtros),
        comunicacoesService.stats(),
      ])

      setComunicacoes(comunicacoesRes.data || [])
      setPagination(prev => ({ ...prev, ...comunicacoesRes.pagination }))
      setStats(statsRes)
    } catch (error) {
      console.error('Erro ao carregar comunicações:', error)
      toast.error('Erro ao carregar comunicações')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    carregarComunicacoes()
  }, [pagination.page, filtroOrigem, filtroStatus, filtroPrioridade, ordenarPor, direcao])

  // Busca com debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (pagination.page === 1) {
        carregarComunicacoes()
      } else {
        setPagination(prev => ({ ...prev, page: 1 }))
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [busca])

  // Dar ciência
  const handleDarCiencia = async () => {
    if (!modalCiencia || !cienciaPor.trim()) {
      toast.error('Informe quem está dando ciência')
      return
    }

    try {
      await comunicacoesService.darCiencia(modalCiencia.id, { ciencia_por: cienciaPor })
      toast.success('Ciência registrada!')
      setModalCiencia(null)
      setCienciaPor('')
      carregarComunicacoes()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao dar ciência')
    }
  }

  // Arquivar
  const handleArquivar = async (id: string) => {
    try {
      await comunicacoesService.arquivar(id)
      toast.success('Comunicação arquivada!')
      carregarComunicacoes()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao arquivar')
    }
  }

  // Formatadores
  const formatarDataHora = (data?: string) => {
    if (!data) return '-'
    return new Date(data).toLocaleString('pt-BR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Cores e badges
  const badgeOrigem = (origem: OrigemComunicacao) => {
    if (origem === 'DJE') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
          <Scale className="w-3 h-3" />
          DJE
        </span>
      )
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
        <Receipt className="w-3 h-3" />
        DEC
      </span>
    )
  }

  const corStatus = (status: string) => {
    const cores: Record<string, string> = {
      NOVA: 'bg-blue-100 text-blue-800',
      PENDENTE: 'bg-amber-100 text-amber-800',
      CIENCIA_DADA: 'bg-green-100 text-green-800',
      ARQUIVADA: 'bg-gray-100 text-gray-800',
    }
    return cores[status] || 'bg-gray-100 text-gray-800'
  }

  const corPrioridade = (prioridade: string) => {
    const cores: Record<string, string> = {
      URGENTE: 'bg-red-100 text-red-800',
      ALTA: 'bg-orange-100 text-orange-800',
      MEDIA: 'bg-yellow-100 text-yellow-800',
      BAIXA: 'bg-green-100 text-green-800',
    }
    return cores[prioridade] || 'bg-gray-100 text-gray-800'
  }

  const textoStatus = (status: string) => {
    const textos: Record<string, string> = {
      NOVA: 'Nova',
      PENDENTE: 'Pendente',
      CIENCIA_DADA: 'Ciência Dada',
      ARQUIVADA: 'Arquivada',
    }
    return textos[status] || status
  }

  const textoTipo = (tipo: string) => {
    const textos: Record<string, string> = {
      INTIMACAO: 'Intimação',
      CITACAO: 'Citação',
      INTIMACAO_FISCAL: 'Intimação Fiscal',
      NOTIFICACAO_FISCAL: 'Notificação Fiscal',
      AVISO_FISCAL: 'Aviso Fiscal',
      COMUNICADO_FISCAL: 'Comunicado Fiscal',
    }
    return textos[tipo] || tipo
  }

  // Identificador da comunicação
  const getIdentificador = (c: Comunicacao) => {
    if (c.origem === 'DJE') {
      return c.numero_processo || c.id_externo
    }
    return c.id_externo
  }

  // Componente de cabeçalho ordenável
  const ColunaOrdenavel = ({ 
    campo, 
    label, 
    className = '' 
  }: { 
    campo: CampoOrdenacao; 
    label: string; 
    className?: string;
  }) => (
    <th 
      className={`px-4 py-3 text-sm font-semibold text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors select-none ${className}`}
      onClick={() => handleOrdenar(campo)}
    >
      <div className={`flex items-center gap-1 ${className.includes('text-center') ? 'justify-center' : ''}`}>
        {label}
        <IconeOrdenacao campo={campo} />
      </div>
    </th>
  )

  return (
    <AppLayout pagina="Comunicações">
      <PageContainer>
        {/* Banner */}
        <PageBanner
          titulo="Comunicações"
          subtitulo="Intimações DJE e comunicações DEC SEFAZ-SP"
          icon={<FileText className="w-6 h-6" />}
          acoes={
            <PageBanner.BotaoSecundario 
              icon={<RefreshCw className="w-4 h-4" />} 
              onClick={carregarComunicacoes} 
              loading={loading}
            >
              Atualizar
            </PageBanner.BotaoSecundario>
          }
        />

        {/* Estatísticas */}
        {stats && (
          <StatCard.Grid colunas={{ mobile: 2, tablet: 3, desktop: 6 }}>
            <StatCard
              label="Total"
              valor={stats.total}
              icon={FileText}
              cor="blue"
              onClick={() => { setFiltroOrigem('todos'); setFiltroStatus('todos') }}
              ativo={filtroOrigem === 'todos' && filtroStatus === 'todos'}
            />
            <StatCard
              label="DJE"
              valor={stats.total_dje || 0}
              icon={Scale}
              cor="purple"
              onClick={() => { setFiltroOrigem('DJE'); setFiltroStatus('todos') }}
              ativo={filtroOrigem === 'DJE'}
            />
            <StatCard
              label="DEC"
              valor={stats.total_dec || 0}
              icon={Receipt}
              cor="blue"
              onClick={() => { setFiltroOrigem('DEC_SEFAZ'); setFiltroStatus('todos') }}
              ativo={filtroOrigem === 'DEC_SEFAZ'}
            />
            <StatCard
              label="Pendentes"
              valor={stats.pendentes}
              icon={Clock}
              cor="amber"
              onClick={() => { setFiltroOrigem('todos'); setFiltroStatus('PENDENTE') }}
              ativo={filtroStatus === 'PENDENTE'}
            />
            <StatCard
              label="Não Lidas"
              valor={stats.nao_lidas || 0}
              icon={Mail}
              cor="red"
            />
            <StatCard
              label="Urgentes"
              valor={stats.urgentes}
              icon={AlertTriangle}
              cor="red"
              onClick={() => { setFiltroOrigem('todos'); setFiltroPrioridade('URGENTE') }}
              ativo={filtroPrioridade === 'URGENTE'}
            />
          </StatCard.Grid>
        )}

        {/* Filtros */}
        <PageSection.Filter>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por processo, cliente, assunto..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F1B3D] focus:border-transparent"
              />
            </div>
            
            {/* Filtro Origem */}
            <select
              value={filtroOrigem}
              onChange={(e) => setFiltroOrigem(e.target.value)}
              className="px-4 py-2.5 border border-gray-300 rounded-lg bg-white"
            >
              <option value="todos">Todas as Origens</option>
              <option value="DJE">DJE - Judicial</option>
              <option value="DEC_SEFAZ">DEC - SEFAZ-SP</option>
            </select>

            {/* Filtro Status */}
            <select
              value={filtroStatus}
              onChange={(e) => setFiltroStatus(e.target.value)}
              className="px-4 py-2.5 border border-gray-300 rounded-lg bg-white"
            >
              <option value="todos">Todos os Status</option>
              <option value="NOVA">Nova</option>
              <option value="PENDENTE">Pendente</option>
              <option value="CIENCIA_DADA">Ciência Dada</option>
              <option value="ARQUIVADA">Arquivada</option>
            </select>

            {/* Filtro Prioridade */}
            <select
              value={filtroPrioridade}
              onChange={(e) => setFiltroPrioridade(e.target.value)}
              className="px-4 py-2.5 border border-gray-300 rounded-lg bg-white"
            >
              <option value="todos">Todas as Prioridades</option>
              <option value="URGENTE">Urgente</option>
              <option value="ALTA">Alta</option>
              <option value="MEDIA">Média</option>
              <option value="BAIXA">Baixa</option>
            </select>
          </div>
        </PageSection.Filter>

        {/* Tabela */}
        <PageSection padding="none">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : comunicacoes.length === 0 ? (
            <PageSection.Empty
              icon={<FileText className="w-12 h-12" />}
              titulo="Nenhuma comunicação encontrada"
              descricao="As comunicações aparecerão aqui conforme forem detectadas no DJE ou DEC"
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <ColunaOrdenavel campo="origem" label="Origem" className="text-left" />
                    <ColunaOrdenavel campo="cliente" label="Cliente" className="text-left" />
                    <ColunaOrdenavel campo="id_externo" label="Identificação" className="text-left" />
                    <ColunaOrdenavel campo="tipo" label="Tipo / Assunto" className="text-left" />
                    <ColunaOrdenavel campo="data_disponibilizacao" label="Data" className="text-left" />
                    <ColunaOrdenavel campo="status" label="Status" className="text-center" />
                    <ColunaOrdenavel campo="prioridade" label="Prioridade" className="text-center" />
                    <th className="text-right px-4 py-3 text-sm font-semibold text-gray-600">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {comunicacoes.map((c) => (
                    <tr 
                      key={c.id} 
                      className={`border-b border-gray-100 hover:bg-gray-50 ${!c.lida ? 'bg-blue-50/30' : ''}`}
                    >
                      {/* Origem */}
                      <td className="px-4 py-3">
                        {badgeOrigem(c.origem)}
                      </td>

                      {/* Cliente */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {!c.lida && <Mail className="w-4 h-4 text-blue-500" />}
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {c.cliente?.razao_social || c.cliente?.nome_fantasia || '-'}
                            </p>
                            <p className="text-xs text-gray-500">{c.cliente?.cnpj}</p>
                          </div>
                        </div>
                      </td>

                      {/* Identificação */}
                      <td className="px-4 py-3">
                        <p className="text-sm font-mono text-gray-700">
                          {getIdentificador(c)}
                        </p>
                        {c.tribunal && (
                          <p className="text-xs text-gray-500">{c.tribunal}</p>
                        )}
                      </td>

                      {/* Tipo / Assunto */}
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-700">{textoTipo(c.tipo)}</p>
                        {c.assunto && (
                          <p className="text-xs text-gray-500 truncate max-w-[200px]" title={c.assunto}>
                            {c.assunto}
                          </p>
                        )}
                      </td>

                      {/* Data */}
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {formatarDataHora(c.data_disponibilizacao)}
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${corStatus(c.status)}`}>
                          {textoStatus(c.status)}
                        </span>
                      </td>

                      {/* Prioridade */}
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${corPrioridade(c.prioridade)}`}>
                          {c.prioridade}
                        </span>
                      </td>

                      {/* Ações */}
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {(c.status === 'NOVA' || c.status === 'PENDENTE') && (
                            <button
                              onClick={() => setModalCiencia({ id: c.id, identificador: getIdentificador(c) })}
                              className="p-2 hover:bg-green-100 rounded-lg transition-colors"
                              title="Dar Ciência"
                            >
                              <Check className="w-4 h-4 text-green-600" />
                            </button>
                          )}
                          {c.status === 'CIENCIA_DADA' && (
                            <button
                              onClick={() => handleArquivar(c.id)}
                              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                              title="Arquivar"
                            >
                              <Archive className="w-4 h-4 text-gray-500" />
                            </button>
                          )}
                          <button
                            onClick={() => navigate(`/comunicacoes/${c.id}`)}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Ver detalhes"
                          >
                            <Eye className="w-4 h-4 text-gray-500" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Paginação */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                Mostrando {(pagination.page - 1) * pagination.limit + 1} -{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Anterior
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.totalPages}
                  className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Próximo
                </button>
              </div>
            </div>
          )}
        </PageSection>

        {/* Modal de Ciência */}
        {modalCiencia && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50" onClick={() => setModalCiencia(null)} />
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6">
              <h3 className="text-lg font-semibold text-[#0F1B3D] mb-2">Dar Ciência</h3>
              <p className="text-sm text-gray-600 mb-4">
                Comunicação: <span className="font-mono">{modalCiencia.identificador}</span>
              </p>
              <input
                type="text"
                value={cienciaPor}
                onChange={(e) => setCienciaPor(e.target.value)}
                placeholder="Nome de quem está dando ciência"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg mb-4 focus:ring-2 focus:ring-[#0F1B3D] focus:border-transparent"
                autoFocus
              />
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setModalCiencia(null)}
                  className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDarCiencia}
                  className="px-4 py-2 text-sm font-medium text-[#0F1B3D] rounded-lg transition-colors hover:opacity-90"
                  style={{ backgroundColor: '#FFB800' }}
                >
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        )}
      </PageContainer>
    </AppLayout>
  )
}

